/**
 * ThermalResponseGraph Component
 * Shows INDOOR COMFORT temperature patterns, not flow temperatures.
 *
 * This graph represents what you FEEL in the room, not what temperature
 * water leaves the boiler at. Indoor comfort is what matters to occupants.
 */

import type { SystemLayout } from './types';
import type { SystemVisualModel } from '../../../../../src/core/types';

interface ThermalResponseGraphProps {
  layout: SystemLayout | null;
  visualModel?: SystemVisualModel;
}

// Indoor temperature constants (what you feel, not flow temp)
const INDOOR_TARGET = 21;     // Target room temperature °C
const INDOOR_MIN = 14;        // Cold start / overnight setback
const INDOOR_MAX = 23;        // Slight overshoot ceiling

/**
 * Convert indoor temperature to Y position on graph
 * Scale: 14°C at bottom, 23°C at top
 */
function tempToY(temp: number, height: number): number {
  const range = INDOOR_MAX - INDOOR_MIN; // 9°C range
  const normalized = (temp - INDOOR_MIN) / range; // 0 to 1
  return height * (1 - normalized); // Flip because Y increases downward
}

/**
 * Generate SVG path for gas boiler indoor comfort pattern
 * Fast rise to target, mild cycling around setpoint
 */
function generateGasComfortPath(width: number, height: number, cyclesPerHour: number = 2): string {
  const responseMinutes = 20; // Gas boilers reach target in ~20 mins
  const totalMinutes = 120;   // 2-hour window
  const risePhaseWidth = (responseMinutes / totalMinutes) * width;

  // Start cold, rise quickly to target
  let path = `M 0 ${tempToY(INDOOR_MIN, height)}`;

  // Fast rise phase (0-20 mins)
  path += ` C ${risePhaseWidth * 0.3} ${tempToY(INDOOR_MIN, height)}, ${risePhaseWidth * 0.6} ${tempToY(INDOOR_TARGET, height)}, ${risePhaseWidth} ${tempToY(INDOOR_TARGET + 0.5, height)}`;

  // Cycling phase - mild oscillation around target (±0.5-1°C)
  const cycleWidth = (width - risePhaseWidth) / Math.max(1, cyclesPerHour);
  let x = risePhaseWidth;

  for (let i = 0; i < cyclesPerHour && x < width; i++) {
    const overshoot = INDOOR_TARGET + 0.8;
    const undershoot = INDOOR_TARGET - 0.5;

    // Slight overshoot
    path += ` L ${x + cycleWidth * 0.25} ${tempToY(overshoot, height)}`;
    // Boiler off, cooling
    path += ` L ${x + cycleWidth * 0.75} ${tempToY(undershoot, height)}`;
    // Boiler on, recovering
    path += ` L ${x + cycleWidth} ${tempToY(INDOOR_TARGET + 0.3, height)}`;

    x += cycleWidth;
  }

  return path;
}

/**
 * Generate SVG path for heat pump indoor comfort pattern
 * Slow, gradual rise over hours, very stable once reached
 */
function generateHeatPumpComfortPath(width: number, height: number): string {
  const responseMinutes = 90;  // Heat pumps take longer to reach target
  const totalMinutes = 120;    // 2-hour window
  const risePhaseWidth = (responseMinutes / totalMinutes) * width;

  // Start cold, rise gradually
  let path = `M 0 ${tempToY(INDOOR_MIN, height)}`;

  // Gradual S-curve rise (longer than gas)
  path += ` C ${risePhaseWidth * 0.2} ${tempToY(INDOOR_MIN + 1, height)}, ${risePhaseWidth * 0.5} ${tempToY(INDOOR_TARGET - 2, height)}, ${risePhaseWidth * 0.75} ${tempToY(INDOOR_TARGET - 0.5, height)}`;

  // Final approach to target
  path += ` C ${risePhaseWidth * 0.85} ${tempToY(INDOOR_TARGET - 0.2, height)}, ${risePhaseWidth * 0.95} ${tempToY(INDOOR_TARGET, height)}, ${risePhaseWidth} ${tempToY(INDOOR_TARGET, height)}`;

  // Steady state - almost perfectly flat (±0.1°C variation)
  path += ` L ${width} ${tempToY(INDOOR_TARGET, height)}`;

  return path;
}

/**
 * Generate SVG path for modulating boiler indoor comfort pattern
 * Good compromise - reasonably fast rise, stable maintenance
 */
function generateModulatingComfortPath(width: number, height: number): string {
  const responseMinutes = 25;
  const totalMinutes = 120;
  const risePhaseWidth = (responseMinutes / totalMinutes) * width;

  let path = `M 0 ${tempToY(INDOOR_MIN, height)}`;

  // Moderate rise
  path += ` C ${risePhaseWidth * 0.3} ${tempToY(INDOOR_MIN + 2, height)}, ${risePhaseWidth * 0.6} ${tempToY(INDOOR_TARGET - 1, height)}, ${risePhaseWidth} ${tempToY(INDOOR_TARGET, height)}`;

  // Stable maintenance with minimal variation (±0.3°C)
  const segments = 4;
  const segmentWidth = (width - risePhaseWidth) / segments;
  let x = risePhaseWidth;

  for (let i = 0; i < segments; i++) {
    const variation = (i % 2 === 0) ? 0.2 : -0.2;
    path += ` L ${x + segmentWidth} ${tempToY(INDOOR_TARGET + variation, height)}`;
    x += segmentWidth;
  }

  return path;
}

export function ThermalResponseGraph({ layout, visualModel }: ThermalResponseGraphProps) {
  const width = 400;
  const height = 200;
  const padding = { left: 45, right: 10, top: 20, bottom: 30 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  // Determine thermal behavior from visual model or layout
  const isHeatPump = visualModel
    ? visualModel.thermalProfile.burnPattern.indexOf('continuous') >= 0
    : layout
    ? layout.thermalBehavior === 'heatPump'
    : false;

  const canModulate = visualModel?.thermalProfile.canModulate ?? false;
  const cyclesPerHour = visualModel?.thermalProfile.typicalCyclesPerHour || 2;

  // Choose appropriate path generator
  let path: string;
  let systemType: 'heatPump' | 'modulating' | 'cycling';

  if (isHeatPump) {
    path = generateHeatPumpComfortPath(graphWidth, graphHeight);
    systemType = 'heatPump';
  } else if (canModulate) {
    path = generateModulatingComfortPath(graphWidth, graphHeight);
    systemType = 'modulating';
  } else {
    path = generateGasComfortPath(graphWidth, graphHeight, cyclesPerHour);
    systemType = 'cycling';
  }

  // Descriptions focused on what you FEEL
  const descriptions = {
    heatPump: 'Heat pumps warm your home gradually over 60-90 minutes, then maintain rock-steady comfort. You won\'t notice temperature swings because there aren\'t any.',
    modulating: 'Modulating boilers adjust their output continuously, reaching comfort in about 25 minutes and holding it steady with minimal variation.',
    cycling: 'Standard boilers heat quickly (15-20 mins) but cycle on/off to maintain temperature. You may notice slight warmth followed by cooling before the next cycle.'
  };

  const titles = {
    heatPump: 'Slow & Steady',
    modulating: 'Quick & Stable',
    cycling: 'Fast with Mild Cycling'
  };

  const colors = {
    heatPump: { line: '#10b981', bg: '#ecfdf5', border: '#d1fae5', text: '#047857' },
    modulating: { line: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
    cycling: { line: '#f59e0b', bg: '#fffbeb', border: '#fde68a', text: '#b45309' }
  };

  const color = colors[systemType];

  return (
    <div style={{
      padding: '16px',
      border: '2px solid #cbd5e1',
      borderRadius: '8px',
      background: 'white'
    }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: 600,
        marginBottom: '4px',
        textAlign: 'center',
        color: '#1e293b'
      }}>
        Indoor Comfort Over Time
      </h3>
      <p style={{
        fontSize: '11px',
        color: '#64748b',
        textAlign: 'center',
        marginBottom: '12px'
      }}>
        What your room temperature feels like after turning on heating
      </p>

      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          background: '#fefefe'
        }}
      >
        {/* Grid lines */}
        <defs>
          <pattern id="comfort-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f1f5f9" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect x={padding.left} y={padding.top} width={graphWidth} height={graphHeight} fill="url(#comfort-grid)" />

        {/* Y-axis labels - INDOOR temperatures */}
        <text x={padding.left - 5} y={padding.top + 5} fontSize="11" fill="#64748b" textAnchor="end">23°C</text>
        <text x={padding.left - 5} y={padding.top + graphHeight * 0.22 + 4} fontSize="11" fill="#059669" fontWeight="600" textAnchor="end">21°C</text>
        <text x={padding.left - 5} y={padding.top + graphHeight * 0.5 + 4} fontSize="11" fill="#64748b" textAnchor="end">18°C</text>
        <text x={padding.left - 5} y={padding.top + graphHeight - 5} fontSize="11" fill="#64748b" textAnchor="end">14°C</text>

        {/* Target temperature line */}
        <line
          x1={padding.left}
          y1={padding.top + tempToY(INDOOR_TARGET, graphHeight)}
          x2={width - padding.right}
          y2={padding.top + tempToY(INDOOR_TARGET, graphHeight)}
          stroke="#059669"
          strokeWidth="1.5"
          strokeDasharray="6,4"
        />
        <text
          x={width - padding.right - 5}
          y={padding.top + tempToY(INDOOR_TARGET, graphHeight) - 5}
          fontSize="10"
          fill="#059669"
          textAnchor="end"
        >
          Target
        </text>

        {/* Comfort response curve */}
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          <path
            d={path}
            fill="none"
            stroke={color.line}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* X-axis labels */}
        <text x={padding.left} y={height - 8} fontSize="10" fill="#64748b">0 min</text>
        <text x={padding.left + graphWidth * 0.25} y={height - 8} fontSize="10" fill="#64748b" textAnchor="middle">30 min</text>
        <text x={padding.left + graphWidth * 0.5} y={height - 8} fontSize="10" fill="#64748b" textAnchor="middle">1 hr</text>
        <text x={padding.left + graphWidth * 0.75} y={height - 8} fontSize="10" fill="#64748b" textAnchor="middle">90 min</text>
        <text x={width - padding.right} y={height - 8} fontSize="10" fill="#64748b" textAnchor="end">2 hrs</text>
      </svg>

      {/* Description */}
      <div style={{
        marginTop: '12px',
        padding: '12px',
        background: color.bg,
        borderRadius: '6px',
        border: `1px solid ${color.border}`,
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          color: color.text,
          marginBottom: '6px'
        }}>
          {titles[systemType]}
        </div>

        <div style={{
          fontSize: '11px',
          color: '#334155',
          lineHeight: '1.5'
        }}>
          {visualModel?.characteristics.behaviorSummary || descriptions[systemType]}
        </div>
      </div>
    </div>
  );
}
